import React, { FC, useContext, useEffect, useState } from "react";
import ConfirmationModal from "common/components/dialogs/ConfirmationModal";
import { Typography } from "@mui/material";
import useUserStore, { IProject } from "common/store/UserStore";
import useNotificationStore from "common/store/NotificationStore";
import { LanguageContext } from "common/contexts/LanguageContext";
import useSettingsStore from "common/store/SettingsStore";
import { useAuthContext } from "common/contexts/AuthContext";
import DialogTextField from "common/components/dialogs/DialogTextField";
import { ValidInputCharsRegEx } from "common/constants/GlobalValues";

interface IProps {
  project: IProject;
  isDeleteConfirmOpen: boolean;
  setIsDeleteConfirmOpen: (isOpen: boolean) => void;
  isRenameConfirmOpen: boolean;
  setIsRenameConfirmOpen: (isOpen: boolean) => void;
}
const ProjectActionConfirmations: FC<IProps> = ({
  project,
  isDeleteConfirmOpen,
  setIsDeleteConfirmOpen,
  isRenameConfirmOpen,
  setIsRenameConfirmOpen,
}) => {
  const [projectName, setProjectName] = useState<string>("");

  const { renameProject, deleteProject } = useUserStore();
  const { settings } = useSettingsStore();
  const { enqueueNotification } = useNotificationStore();
  const { getString } = useContext(LanguageContext);
  const { getAccessToken } = useAuthContext();

  useEffect(() => {
    setProjectName(project.projectName);
  }, [project]);

  const handleDeleteConfirmation = async () => {
    const response = await deleteProject(settings.apiBaseUrl, getAccessToken, project);
    if (response.succeeded) {
      enqueueNotification("delete_project_success", `${getString('dlg_del_proj_success')} ${project.projectName}`, "success");
    } else {
      enqueueNotification("delete_project_failure", response.errorMessage!, "error");
    }
    setIsDeleteConfirmOpen(false);
  };

  const handleRenameConfirmation = async () => {
    if (!ValidInputCharsRegEx.test(projectName)) {
      enqueueNotification("invalid_chars", `${getString('msg_invalid_chars')} ${getString('ProjectName')}`, "error");
      return;
    }

    const response = await renameProject(settings.apiBaseUrl, getAccessToken, project, projectName);
    if (response.succeeded) {
      enqueueNotification("rename_project_success", `${getString('dlg_rename_proj_success')} ${projectName}`, "success");
    } else {
      enqueueNotification("rename_project_failure", response.errorMessage!, "error");
    }
    setIsRenameConfirmOpen(false);
  };

  return (
    <>
      <ConfirmationModal
        isOpen={isDeleteConfirmOpen}
        handleClose={() => {
          setIsDeleteConfirmOpen(false);
        }}
        handleConfirm={handleDeleteConfirmation}
        title={getString("dlg_del_proj_title")}
        maxWidth="sm"
        hideDivider={true}
      >
        <Typography variant="subtitle1" sx={{ pt: "24px" }}>
          {getString("dlg_del_proj_confirm")} '{project.projectName}'?
        </Typography>
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={isRenameConfirmOpen}
        handleClose={() => {
          setIsRenameConfirmOpen(false);
        }}
        handleConfirm={handleRenameConfirmation}
        title={getString("dlg_rename_proj_title")}
        maxWidth="sm"
        confirmBtnLabel={getString("cmn_save")}
        hideDivider={true}
      >
        <DialogTextField
          sx={{ pt: "24px" }}
          variant="filled"
          size="small"
          label={getString("dlg_rename_proj_name_lbl")}
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
        />
      </ConfirmationModal>
    </>
  );
};

export default ProjectActionConfirmations;
